import React, { useEffect, useMemo, useState } from "react";
import {
  CircularProgress,
  FormControl,
  InputAdornment,
  MenuItem,
  Pagination,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import { BookingApi } from "../../API/Apis/BookingApi";
import { t } from "i18next";
import { alpha, createTheme, Theme } from "@mui/material/styles";
import { formatThemeForReservedArea } from "../../config/MuiColorThemes";
import DownloadIcon from "@mui/icons-material/Download";
import { Search } from "@mui/icons-material";
import AlertMessage from "../AlertMessage";
import DefaultPage from "../../components/Layouts/DefaultPage";

// ── Date formatter  →   ─────────────────────────────────
const formatDate = (value: string | null | undefined): string => {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return value;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = String(d.getFullYear()).slice(2);
  return `${dd}/${mm}/${yy}`;
};

// ── Status helpers ──────────────────────────────────────────────
type DateFilter = "30d" | "3m" | "12m";
type StatusFilter = "all" | "paid" | "unpaid" | "expired";

const getInvoiceStatus = (invoice: any): "paid" | "unpaid" | "expired" => {
  if (invoice.isPaid) return "paid";
  if (invoice.invoiceEndDate) {
    const endDate = new Date(invoice.invoiceEndDate);
    if (endDate < new Date()) return "expired";
  }
  return "unpaid";
};

const statusConfig: Record<
  "paid" | "unpaid" | "expired",
  { label: string; backgroundColor: string; color: string }
> = {
  paid: {
    label: t("Paid"),
    backgroundColor: "#dcfce7",
    color: "#16a34a",
  },
  unpaid: {
    label: t("Unpaid"),
    backgroundColor: "#fef9c3",
    color: "#ca8a04",
  },
  expired: {
    label: t("Expired"),
    backgroundColor: "#fee2e2",
    color: "#dc2626",
  },
};

const TABLE_HEADERS = [
  t("Invoice Number"),
  t("Start Date"),
  t("End Date"),
  t("Total"),
  t("Status"),
  "",
];

// ── Component ────────────────────────────────────────────────────
export default function InvoiceCard() {
  const [allInvoiceList, setAllInvoiceList] = useState<any[]>([]);
  const [loader, setLoader] = useState(true);
  const [page, setPage] = useState(1);
  const rowsPerPage = 8;
  const [openModal, setOpenModal] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [dynamicTheme, setDynamicTheme] = useState<Theme | null>(null);

  // Filters & search
  const [searchInvoice, setSearchInvoice] = useState("");
  const [sortOrder, setSortOrder] = useState<"recent" | "oldest">("recent");
  const [dateFilter, setDateFilter] = useState<DateFilter>("12m");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");

  const isSmallScreen = window.innerWidth <= 768;

  useEffect(() => {
    async function fetchTheme() {
      const dynamicPalette = await formatThemeForReservedArea();
      setDynamicTheme(createTheme(dynamicPalette));
    }
    fetchTheme();
  }, []);

  const primaryColor = dynamicTheme?.palette?.primary?.main || "#2cb133";

  useEffect(() => {
    setLoader(true);
    const id: any = localStorage.getItem("userID");
    BookingApi.getOnlineUserInvoices(id, 0, 10000).then((e) => {
      setAllInvoiceList(e.data.invoiceList || []);
      setLoader(false);
    });
  }, []);

  // ── Download ──
  const downloadInvoice = (invoiceId: any) => {
    if (invoiceId === "No Invoice Found") {
      setModalMessage("No Invoice Found");
      setOpenModal(true);
    } else {
      BookingApi.downloadInvoice(invoiceId).then((e) => {
        if (e.data.status === "error") {
          setModalMessage(e.data.errorMessage);
          setOpenModal(true);
        } else {
          window.open(e.data, "_blank");
        }
      });
    }
  };

  // ── filtering + sorting ──
  const filteredInvoices = useMemo(() => {
    let list = [...allInvoiceList];

    // Search
    if (searchInvoice.trim()) {
      const term = searchInvoice.trim().toLowerCase();
      list = list.filter((inv) => {
        const invoiceNo = `${inv.prefix}-${inv.invoiceNumber}`.toLowerCase();
        const reservation = String(inv.reservation || "").toLowerCase();
        const rental = String(inv.rentalAgreement || "").toLowerCase();
        const amount = String(inv.totInvoice || "");
        return (
          invoiceNo.includes(term) ||
          reservation.includes(term) ||
          rental.includes(term) ||
          amount.includes(term)
        );
      });
    }

    // Date range filter
    const now = new Date();
    const cutoff =
      dateFilter === "30d"
        ? new Date(new Date().setDate(now.getDate() - 30))
        : dateFilter === "3m"
        ? new Date(new Date().setMonth(now.getMonth() - 3))
        : new Date(new Date().setFullYear(now.getFullYear() - 1));

    list = list.filter((inv) => {
      if (!inv.startDateMovement) return true;
      return new Date(inv.startDateMovement) >= cutoff;
    });

    // Status filter
    if (statusFilter !== "all") {
      list = list.filter((inv) => getInvoiceStatus(inv) === statusFilter);
    }

    // Sort
    list.sort((a, b) => {
      const da = new Date(a.startDateMovement || 0).getTime();
      const db = new Date(b.startDateMovement || 0).getTime();
      return sortOrder === "oldest" ? da - db : db - da;
    });

    return list;
  }, [allInvoiceList, searchInvoice, dateFilter, statusFilter, sortOrder]);

  // ── Pagination  ──
  const totalPages = Math.ceil(filteredInvoices.length / rowsPerPage);
  const rangeStart =
    filteredInvoices.length === 0 ? 0 : (page - 1) * rowsPerPage + 1;
  const rangeEnd = Math.min(page * rowsPerPage, filteredInvoices.length);
  const pagedInvoices = filteredInvoices.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  const handlePageChange = (_: React.ChangeEvent<unknown>, newPage: number) => {
    setPage(newPage);
  };

  // Reset to page 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchInvoice, dateFilter, statusFilter, sortOrder]);

  // ── Filter button styles ──
  const filterBtnStyle = (active: boolean): React.CSSProperties => ({
    padding: "6px 14px",
    borderRadius: "6px",
    border: `1px solid ${active ? primaryColor : "#d1d5db"}`,
    backgroundColor: active ? alpha(primaryColor, 0.1) : "#fff",
    color: active ? primaryColor : "#555",
    fontWeight: active ? 600 : 400,
    fontSize: "13px",
    cursor: "pointer",
    transition: "all 0.15s",
  });

  const dateFilterButtons: { label: string; value: DateFilter }[] = [
    { label: t("Last 30 days"), value: "30d" },
    { label: t("3 months"), value: "3m" },
    { label: t("12 months"), value: "12m" },
  ];

  const statusFilterButtons: { label: string; value: StatusFilter }[] = [
    { label: t("Paid"), value: "paid" },
    { label: t("Unpaid"), value: "unpaid" },
    { label: t("Expired"), value: "expired" },
  ];

  const dateFilterLabel =
    dateFilter === "30d"
      ? t("Last 30 days")
      : dateFilter === "3m"
      ? t("Last 3 months")
      : t("Last 12 months");

  // ── Pagination UI (shared) ──
  const PaginationBar = () => {
    if (totalPages <= 1) return null;
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginTop: "20px",
          gap: "12px",
          flexWrap: "wrap",
        }}
      >
        <span style={{ fontSize: "14px", color: "#6b7280" }}>
          {rangeStart}–{rangeEnd} {t("of")} {filteredInvoices.length}
        </span>
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
          size="small"
          sx={{
            "& .MuiPaginationItem-root.Mui-selected": {
              backgroundColor: alpha(primaryColor, 0.1),
              color: primaryColor,
              fontWeight: 400,
              "&:hover": {
                backgroundColor: alpha(primaryColor, 0.18),
              },
            },
            "& .MuiPaginationItem-root": {
              "&:hover": {
                backgroundColor: alpha(primaryColor, 0.06),
              },
            },
          }}
        />
      </div>
    );
  };

  return (
    <>
      <AlertMessage
        openModal={openModal}
        requestOnClose={() => {
          setOpenModal(false);
          setModalMessage("");
        }}
        modalMessage={modalMessage}
      />

      <div
        style={{
          backgroundColor: "#fff",
          padding: isSmallScreen ? "20px 16px" : "10px 100px",
          boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
          height: "100%",
        }}
      >
        {/* ── Title ── */}
        <p
          style={{
            fontSize: isSmallScreen ? "22px" : "26px",
            fontWeight: 700,
            color: "#222",
            lineHeight: 1.2,
            marginTop: "10px",
          }}
        >
          {t("Invoice List")}
        </p>
        <p style={{ fontSize: "14px", color: "#9ca3af", margin: "6px 0 20px" }}>
          {t("You have")} {filteredInvoices.length}{" "}
          {t("Invoice List").toLowerCase()} | {dateFilterLabel}
        </p>

        {/* ── Search + Sort row ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            marginBottom: "16px",
            flexWrap: isSmallScreen ? "wrap" : "nowrap",
          }}
        >
          <TextField
            placeholder={t(
              "Search by invoice no., booking no., license plate, amount..."
            )}
            variant="outlined"
            size="small"
            value={searchInvoice}
            onChange={(e) => setSearchInvoice(e.target.value)}
            style={{
              flex: 1,
              maxWidth: isSmallScreen ? "100%" : "50%",
              minWidth: isSmallScreen ? "100%" : "auto",
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search style={{ color: "#9ca3af", fontSize: "20px" }} />
                </InputAdornment>
              ),
              style: { borderRadius: "8px", fontSize: "14px" },
            }}
          />
          {!isSmallScreen && (
            <FormControl size="small" style={{ minWidth: "220px" }}>
              <Select
                value={sortOrder}
                onChange={(e) =>
                  setSortOrder(e.target.value as "recent" | "oldest")
                }
                displayEmpty
                style={{ borderRadius: "8px", fontSize: "14px" }}
              >
                <MenuItem value="recent">
                  {t("Sort by date (most recent)")}
                </MenuItem>
                <MenuItem value="oldest">{t("Sort by date (oldest)")}</MenuItem>
              </Select>
            </FormControl>
          )}
        </div>

        {/* ── Date filter + Status filter row ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "8px",
            marginBottom: "20px",
          }}
        >
          {/* Date tabs */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {dateFilterButtons.map((btn) => (
              <button
                key={btn.value}
                onClick={() => setDateFilter(btn.value)}
                style={filterBtnStyle(dateFilter === btn.value)}
              >
                {btn.label}
              </button>
            ))}
          </div>

          {/* Status filter */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            {statusFilterButtons.map((btn) => (
              <button
                key={btn.value}
                onClick={() =>
                  setStatusFilter((prev) =>
                    prev === btn.value ? "all" : btn.value
                  )
                }
                style={filterBtnStyle(statusFilter === btn.value)}
              >
                {btn.label}
              </button>
            ))}
          </div>
        </div>

        {/* ── Content ── */}
        {loader ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "40px 0",
            }}
          >
            <CircularProgress style={{ color: primaryColor }} />
          </div>
        ) : filteredInvoices.length === 0 ? (
          <DefaultPage message={t("You haven't created any invoices yet.")} />
        ) : !isSmallScreen ? (
          /* ── Desktop table ── */
          <>
            <TableContainer
              style={{
                borderRadius: "8px",
                overflow: "hidden",
                border: "1px solid #f0f0f0",
              }}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {TABLE_HEADERS.map((header, index) => (
                      <TableCell
                        key={index}
                        style={{
                          backgroundColor: "#f8f9fa",
                          color: "#9ca3af",
                          fontWeight: 400,
                          fontSize: "14px",
                          borderBottom: "none",
                          padding: header === "" ? "12px 8px" : "12px 16px",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {header ? t(header) : ""}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedInvoices.map((inv: any, i: number) => {
                    const status = getInvoiceStatus(inv);
                    const sc = statusConfig[status];
                    return (
                      <TableRow
                        key={i}
                        style={{
                          backgroundColor: i % 2 === 0 ? "#fff" : "#fafafa",
                        }}
                      >
                        {/* Invoice number */}
                        <TableCell style={{ padding: "12px 16px" }}>
                          <p
                            style={{
                              margin: 0,
                              fontWeight: 600,
                              fontSize: "13px",
                              color: "#222",
                            }}
                          >
                            {inv.prefix}-{inv.invoiceNumber}
                          </p>
                          <p
                            style={{
                              margin: 0,
                              fontSize: "11px",
                              color: "#9ca3af",
                              marginTop: "2px",
                            }}
                          >
                            {inv.rentalAgreement
                              ? `Rental ${inv.rentalAgreement}`
                              : `Reservation ${inv.reservation}`}
                          </p>
                        </TableCell>

                        {/* Start date */}
                        <TableCell
                          style={{
                            fontSize: "13px",
                            color: "#444",
                            padding: "12px 16px",
                          }}
                        >
                          {formatDate(inv.startDateMovement)}
                        </TableCell>

                        {/* End date */}
                        <TableCell
                          style={{
                            fontSize: "13px",
                            color: "#444",
                            padding: "12px 16px",
                          }}
                        >
                          {formatDate(inv.endDateMovement)}
                        </TableCell>

                        {/* Total */}
                        <TableCell
                          style={{
                            fontSize: "13px",
                            color: "#444",
                            padding: "12px 16px",
                            fontWeight: 500,
                          }}
                        >
                          € {inv.totInvoice || "—"}
                        </TableCell>

                        {/* Status badge */}
                        <TableCell style={{ padding: "12px 16px" }}>
                          <span
                            style={{
                              backgroundColor: sc.backgroundColor,
                              color: sc.color,
                              borderRadius: "6px",
                              padding: "4px 10px",
                              fontSize: "12px",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                            }}
                          >
                            {t(sc.label)}
                          </span>
                        </TableCell>

                        {/* Download */}
                        <TableCell style={{ padding: "12px 8px" }}>
                          <button
                            onClick={() => downloadInvoice(inv.id)}
                            title={t("Download")}
                            style={{
                              background: "none",
                              border: "none",
                              borderRadius: "6px",
                              padding: "5px 8px",
                              cursor: "pointer",
                              color: "#6b7280",
                              display: "flex",
                              alignItems: "center",
                              transition: "border-color 0.15s",
                            }}
                            onMouseEnter={(e) =>
                              ((
                                e.currentTarget as HTMLButtonElement
                              ).style.backgroundColor = `${primaryColor}18`)
                            }
                            onMouseLeave={(e) =>
                              ((
                                e.currentTarget as HTMLButtonElement
                              ).style.backgroundColor = "transparent")
                            }
                          >
                            <DownloadIcon style={{ fontSize: "16px" }} />
                          </button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <PaginationBar />
          </>
        ) : (
          /* ── Mobile card layout ── */
          <div
            style={{ display: "flex", flexDirection: "column", gap: "12px" }}
          >
            {pagedInvoices.map((inv: any, i: number) => {
              const status = getInvoiceStatus(inv);
              const sc = statusConfig[status];
              return (
                <div
                  key={i}
                  style={{
                    backgroundColor: "#fff",
                    borderRadius: "10px",
                    overflow: "hidden",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                    border: "1px solid #f0f0f0",
                  }}
                >
                  {/* Card header — same alpha(primary,0.1) as customerDetailsList */}
                  <div
                    style={{
                      backgroundColor: alpha(primaryColor, 0.1),
                      padding: "8px 14px",
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span
                      style={{
                        color: primaryColor,
                        fontWeight: 400,
                        fontSize: "13px",
                      }}
                    >
                      {inv.prefix}-{inv.invoiceNumber}
                    </span>
                    <span
                      style={{
                        backgroundColor: sc.backgroundColor,
                        color: sc.color,
                        borderRadius: "6px",
                        padding: "2px 8px",
                        fontSize: "11px",
                        fontWeight: 600,
                      }}
                    >
                      {t(sc.label)}
                    </span>
                  </div>

                  {/* Card body */}
                  <div style={{ padding: "12px 14px" }}>
                    {[
                      {
                        label: t("Reference"),
                        value: inv.rentalAgreement
                          ? `Rental ${inv.rentalAgreement}`
                          : `Reservation ${inv.reservation}`,
                      },
                      {
                        label: t("Start Date"),
                        value: formatDate(inv.startDateMovement),
                      },
                      {
                        label: t("End Date"),
                        value: formatDate(inv.endDateMovement),
                      },
                      {
                        label: t("Total"),
                        value: `€ ${inv.totInvoice || "—"}`,
                      },
                    ].map((row, idx) => (
                      <div
                        key={idx}
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          fontSize: "13px",
                          padding: "6px 0",
                          borderBottom: idx < 3 ? "1px solid #f3f4f6" : "none",
                        }}
                      >
                        <span style={{ color: "#9ca3af" }}>{row.label}</span>
                        <span style={{ color: "#222", fontWeight: 500 }}>
                          {row.value}
                        </span>
                      </div>
                    ))}

                    {/* Download button */}
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "flex-end",
                        marginTop: "10px",
                      }}
                    >
                      <button
                        onClick={() => downloadInvoice(inv.id)}
                        style={{
                          background: "none",
                          border: `1px solid ${primaryColor}`,
                          borderRadius: "6px",
                          padding: "5px 12px",
                          cursor: "pointer",
                          color: primaryColor,
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          fontSize: "12px",
                          fontWeight: 500,
                        }}
                      >
                        <DownloadIcon style={{ fontSize: "14px" }} />
                        {t("Download")}
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}

            <PaginationBar />
          </div>
        )}
      </div>
    </>
  );
}
